import { useCallback, useEffect, useRef, useState } from 'react';
import { getChangedStateCodes } from 'helpers/carriers/carriers';

const EMPTY_SET = new Set();
const PULSE_STEP_MS = 600;
const PULSE_COUNT = 3;
const REDUCED_MOTION_HOLD_MS = 3000;

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * pulses the states whose carrier list changed after the filter changed, for a few seconds
 * @param stateMap - `{ [stateCode]: { carriers } }` for every state
 * @param filterKey - string that changes when the filter selection changes; data reloads alone don't pulse
 * @param paused - cancels the running pulse (and blocks new ones) while true, e.g. on legend hover
 * @returns the set of state codes that are lit right now
 */
export default function useChangedStatePulse({ stateMap, filterKey, paused }) {
  const [litCodes, setLitCodes] = useState(EMPTY_SET);
  const previousRef = useRef({ stateMap, filterKey });
  const timersRef = useRef([]);

  const stop = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setLitCodes(EMPTY_SET);
  }, []);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { stateMap, filterKey };
    if (previous.filterKey === filterKey || paused) return;

    const changed = getChangedStateCodes(previous.stateMap, stateMap);
    stop();
    if (changed.size === 0) return;

    const schedule = (codes, delay) =>
      timersRef.current.push(setTimeout(() => setLitCodes(codes), delay));

    if (prefersReducedMotion()) {
      schedule(changed, 0);
      schedule(EMPTY_SET, REDUCED_MOTION_HOLD_MS);
      return;
    }
    for (let step = 0; step < PULSE_COUNT * 2; step += 1) {
      schedule(step % 2 === 0 ? changed : EMPTY_SET, step * PULSE_STEP_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateMap, filterKey]);

  useEffect(() => {
    if (paused) stop();
  }, [paused, stop]);

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  return litCodes;
}
